/**
 * Token produced by the lexer. Each token represents a lexical element in the expression.
 *
 * Token Types:
 * - **ident**: Identifiers (variable names, property names)
 * - **keyword**: Keywords (let, match, true, false, null, debugger)
 * - **number**: Numeric literals (42, 3.14, 1_000)
 * - **string**: String literals ("hello", 'world')
 * - **op**: Operators (+, -, *, /, %, >, <, ==, !=, ===, !==, &&, ||, !, ?, :, **)
 * - **paren**: Parentheses ( )
 * - **dot**: Dot operator for property access (.)
 * - **comma**: Comma separator (,)
 * - **semi**: Semicolon for statement separation (;)
 * - **equals**: Assignment operator (=)
 * - **bracket**: Square brackets for arrays [ ]
 * - **brace**: Curly braces for objects { }
 * - **arrow**: Arrow operator for match patterns (=>)
 * - **pipe**: Pipeline operator (|>)
 * - **question**: Question mark for ternary/optional (?, used in ?. and ternary)
 * - **currency**: Currency literals ($19.99, £5.00, €10.50)
 * - **date**: ISO date literals (2024-01-15, 2024-03-20T10:30:00, 2024-01-15T10:30:00Z)
 * - **type**: Type operator (type)
 * - **formula**: Excel-like formula wrapper (@[...])
 */
export interface Token {
  type:
    | 'ident'
    | 'keyword'
    | 'number'
    | 'string'
    | 'template'
    | 'op'
    | 'paren'
    | 'dot'
    | 'comma'
    | 'semi'
    | 'equals'
    | 'bracket'
    | 'brace'
    | 'arrow'
    | 'pipe'
    | 'question'
    | 'currency'
    | 'date'
    | 'type'
    | 'formula'
    | 'comment';
  value: string;
  index: number;
  length: number;
  currency?: string;
}

/** A lexical failure with an exact source range for editor diagnostics. */
export class ExpressionLexError extends Error {
  constructor(
    message: string,
    public readonly index: number,
    public readonly length = 1,
  ) {
    super(message);
    this.name = 'ExpressionLexError';
  }
}

function isValidIsoDateLiteral(value: string): boolean {
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?(?:Z|([+-])(\d{2}):(\d{2}))?)?$/,
  );
  if (!match) return false;
  const [
    ,
    yearText,
    monthText,
    dayText,
    hourText,
    minuteText,
    secondText,
    ,
    ,
    offsetHourText,
    offsetMinuteText,
  ] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const maxDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (month < 1 || month > 12 || day < 1 || day > maxDay) return false;
  if (hourText === undefined) return true;
  if (
    Number(hourText) > 23 ||
    Number(minuteText) > 59 ||
    Number(secondText) > 59
  )
    return false;
  if (
    offsetHourText !== undefined &&
    (Number(offsetHourText) > 23 || Number(offsetMinuteText) > 59)
  )
    return false;
  return true;
}

/**
 * Tokenise a DSL expression string into an array of `Token` objects.
 *
 * This is the first stage of the expression pipeline:
 * `lexExpression` → `compileExpression` (parser) → `evalNode` (evaluator)
 *
 * You rarely need to call this directly - `compileExpression` and
 * `evaluateExpression` call it internally. It is exposed for IDE integrations
 * (Monaco token providers, syntax colouring, hover tooltips) and for the
 * diagnostic parser (`parseExpressionDetailed`).
 *
 * @param src  The raw expression source string.
 * @returns    An ordered array of tokens. Never returns null; throws on
 *             unterminated strings or unrecognised characters.
 *
 * @example
 * lexExpression('value + 2');
 * // → [ { type: 'ident', value: 'value' }, { type: 'op', value: '+' }, { type: 'number', value: '2' } ]
 *
 * @example
 * lexExpression('match x { 1..=5 => "low", _ => "high" }');
 * // Returns tokens including the `..=` range operator and `=>` arrow
 */
export function lexExpression(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;

  while (i < src.length) {
    const c = src.charAt(i);

    if (/\s/.test(c)) {
      i++;
      continue;
    }

    /**
     * @lexer comment - Single-line comments
     * @syntax # comment text (until end of line)
     * @example # This is a comment
     * @example value + 5 # Add five to value
     * @note Comments are ignored during parsing
     * @note Inside @[...] formulas, comments stop at ] to preserve formula closing bracket
     */
    if (c === '#') {
      const start = i;
      while (
        i < src.length &&
        src.charAt(i) !== '\n' &&
        src.charAt(i) !== ']'
      ) {
        i++;
      }
      out.push({
        type: 'comment',
        value: src.slice(start, i),
        index: start,
        length: i - start,
      });
      continue;
    }

    /**
     * @lexer date - ISO 8601 date/datetime literals
     * @syntax YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss[.sss][Z|±HH:mm]
     * @example 2024-01-15, 2024-03-20T10:30:00, 2024-01-15T10:30:00Z, 2024-01-15T10:30:00.123+05:30
     * @note Dates are automatically parsed as Date objects
     */
    if (/[0-9]/.test(c) && i + 9 < src.length) {
      const dateMatch = src
        .slice(i)
        .match(
          /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?(?:Z|[+-]\d{2}:\d{2})?)?/,
        );
      if (dateMatch) {
        const start = i;
        const dateStr = dateMatch[0];
        if (!isValidIsoDateLiteral(dateStr)) {
          throw new ExpressionLexError(
            `Invalid ISO date literal '${dateStr}'`,
            start,
            dateStr.length,
          );
        }
        i += dateStr.length;
        out.push({
          type: 'date',
          value: dateStr,
          index: start,
          length: dateStr.length,
        });
        continue;
      }
    }

    /**
     * @lexer string - String literals with single or double quotes
     * @syntax "text" or 'text'
     * @example "hello world", 'user input', "it's working"
     */
    if (c === '"' || c === "'") {
      const start = i;
      const q = c;
      i++;
      let s = '';
      while (i < src.length && src.charAt(i) !== q) {
        if (src.charAt(i) === '\\') {
          const _escapeIndex = i;
          i++;
          if (i >= src.length)
            throw new ExpressionLexError(
              'Unterminated string literal',
              start,
              src.length - start,
            );
          const escaped = src.charAt(i);
          const escapes: Record<string, string> = {
            n: '\n',
            r: '\r',
            t: '\t',
            b: '\b',
            f: '\f',
            v: '\v',
            '0': '\0',
            '\\': '\\',
            '"': '"',
            "'": "'",
          };
          if (!(escaped in escapes)) {
            // Unknown escapes pass through VERBATIM (backslash kept): schema
            // expressions embed regex patterns in string literals ("\d{3}",
            // "\s+", ...) and existing production schemas rely on this.
            // Throwing here broke every regexReplace/regexMatch transform.
            s += `\\${escaped}`;
            i++;
            continue;
          }
          s += escapes[escaped];
          i++;
          continue;
        }
        s += src.charAt(i);
        i++;
      }
      if (src.charAt(i) !== q)
        throw new ExpressionLexError(
          'Unterminated string literal',
          start,
          src.length - start,
        );
      i++;
      out.push({ type: 'string', value: s, index: start, length: i - start });
      continue;
    }

    /**
     * @lexer currency - Currency literals with symbol prefix
     * @syntax $amount, £amount, €amount, ¥amount, ₹amount, etc.
     * @example $19.99, £5.00, €10.50, ¥1000, ₹500.50
     * @note Currency values automatically use Decimal.js for precision
     */
    if (
      /[£€¥₹₽₩₪¢₱₦₡₴]/.test(c) ||
      (c === '$' && i + 1 < src.length && /[0-9]/.test(src.charAt(i + 1)))
    ) {
      const start = i;
      const currencySymbol = c;
      i++;

      let n = '';
      let hasDot = false;
      while (i < src.length) {
        const ch = src.charAt(i);
        if (
          /[0-9]/.test(ch) ||
          (ch === '_' &&
            /[0-9]/.test(src.charAt(i - 1)) &&
            /[0-9]/.test(src.charAt(i + 1)))
        ) {
          n += ch;
          i++;
        } else if (ch === '.' && !hasDot) {
          const nextCh = src.charAt(i + 1);
          if (/[0-9]/.test(nextCh)) {
            n += ch;
            hasDot = true;
            i++;
          } else {
            break;
          }
        } else {
          break;
        }
      }

      if (n === '' && currencySymbol !== '$') {
        throw new ExpressionLexError(
          `Currency symbol '${currencySymbol}' must be followed by a number`,
          start,
          1,
        );
      }

      out.push({
        type: 'currency',
        value: n,
        currency: currencySymbol,
        index: start,
        length: i - start,
      });
      continue;
    }

    /**
     * @lexer number - Numeric literals (integers, decimals, with optional underscores)
     * @syntax digits[._digits]*
     * @example 42, 3.14, 1_000, 0.5, 123_456.789
     * @note All numbers use Decimal.js internally for precision
     */
    if (/[0-9]/.test(c)) {
      const start = i;
      let n = c;
      i++;
      let hasDot = false;
      while (i < src.length) {
        const ch = src.charAt(i);
        if (
          /[0-9]/.test(ch) ||
          (ch === '_' &&
            /[0-9]/.test(src.charAt(i - 1)) &&
            /[0-9]/.test(src.charAt(i + 1)))
        ) {
          n += ch;
          i++;
        } else if (ch === '.' && !hasDot) {
          const nextCh = src.charAt(i + 1);
          if (/[0-9]/.test(nextCh)) {
            n += ch;
            hasDot = true;
            i++;
          } else {
            break;
          }
        } else {
          break;
        }
      }
      out.push({ type: 'number', value: n, index: start, length: i - start });
      continue;
    }

    /**
     * @operator === - Strict equality (checks type and value) - Example: `status === "active"`
     * @operator !== - Strict inequality (checks type and value) - Example: `status !== "pending"`
     */
    if (src.startsWith('===', i) || src.startsWith('!==', i)) {
      out.push({ type: 'op', value: src.slice(i, i + 3), index: i, length: 3 });
      i += 3;
      continue;
    }

    /** @operator power - Exponentiation (power) - Example: `base ** exponent`, `2 ** 8 = 256`
     * @syntax **
     */
    if (src.startsWith('**', i)) {
      out.push({ type: 'op', value: '**', index: i, length: 2 });
      i += 2;
      continue;
    }

    /** @operator ?. - Optional chaining (safe property access) - Example: `user?.address?.city` - Returns undefined if any part is null/undefined */
    if (src.startsWith('?.', i)) {
      out.push({ type: 'op', value: '?.', index: i, length: 2 });
      i += 2;
      continue;
    }

    /** @operator |> - Pipeline operator (function chaining, left-to-right); an operator after a stage works on its answer - Example: `value |> abs |> round` equivalent to `round(abs(value))`, `xs |> average * 100` equivalent to `average(xs) * 100` */
    if (src.startsWith('|>', i)) {
      out.push({ type: 'pipe', value: '|>', index: i, length: 2 });
      i += 2;
      continue;
    }
    /** @operator | - OR separator in match patterns - Example: `1 | 2 | 3 => "few"` */
    if (c === '|' && !src.startsWith('||', i)) {
      out.push({ type: 'op', value: '|', index: i, length: 1 });
      i++;
      continue;
    }

    /**
     * @lexer arrow - Arrow operator for function definitions and match patterns
     * @syntax =>
     * @example (x) => x * 2  // Arrow function
     * @example (a, b) => a + b  // Multi-parameter arrow function
     * @example (str) => { upper(str) }  // Arrow function with block body
     * @example match x { 1 => "one", _ => "other" }  // Pattern matching
     * @note In arrow functions: creates lambda with implicit return
     * @note In match expressions: separates pattern from result
     */
    if (src.startsWith('=>', i)) {
      out.push({ type: 'arrow', value: '=>', index: i, length: 2 });
      i += 2;
      continue;
    }

    /**
     * @operator && - Logical AND (short-circuit evaluation) - Example: `age >= 18 && hasLicense`
     * @operator and - Natural-language AND keyword, identical to `&&`.
     * Lexed as a `keyword` token; the parser normalises it to `&&` in the AST.
     * @syntax left and right
     * @example age >= 18 and hasConsent
     * @example value >= 5 and <= 10
     * @note Enables shorthand comparison: `a >= 5 and <= 10` desugars to `a >= 5 && a <= 10`.
     */
    if (src.startsWith('&&', i)) {
      out.push({ type: 'op', value: '&&', index: i, length: 2 });
      i += 2;
      continue;
    }

    /**
     * @operator || - Logical OR (short-circuit evaluation) - Example: `isAdmin || isOwner`
     * @operator or - Natural-language OR keyword, identical to `||`.
     * Lexed as a `keyword` token; the parser normalises it to `||` in the AST.
     * @syntax left or right
     * @example isAdmin or isOwner
     * @example value >= 5 or <= 10
     * @note Enables shorthand comparison: `a >= 5 or <= 10` desugars to `a >= 5 || a <= 10`.
     */
    if (src.startsWith('||', i)) {
      out.push({ type: 'op', value: '||', index: i, length: 2 });
      i += 2;
      continue;
    }

    /**
     * @operator shorthand-comparison - Implicit-subject shorthand for chained comparisons.
     * When a logical operator (`||`, `&&`, `??`, `or`, `and`) is immediately followed by a
     * relational/equality operator, the subject of the preceding comparison is reused automatically.
     * @syntax subject op1 value1 LOGICAL op2 value2
     * @example value >= 5 or <= 10         // same as: value >= 5 || value <= 10
     * @example age >= 18 and <= 65         // same as: age >= 18 && age <= 65
     * @example score >= 0 and <= 100       // range check with one subject
     * @example x + 1 >= 5 and <= 10        // full expression as implied subject
     * @example score >= 2 and <= 8 and != 5 // chained - applies recursively
     * @note Activates only when a comparison op (`>`, `>=`, `<`, `<=`, `==`, `!=`, `===`, `!==`) immediately follows the logical op.
     * @note Normal usage such as `a > 5 or b > 2` is unaffected; the implied subject is NOT used.
     * @operator ?? - Nullish coalescing (returns right side if left is null/undefined) - Example: `value ?? defaultValue`
     */
    if (src.startsWith('??', i)) {
      out.push({ type: 'op', value: '??', index: i, length: 2 });
      i += 2;
      continue;
    }

    /**
     * @operator >= - Greater than or equal - Example: `age >= 18`
     * @operator <= - Less than or equal - Example: `score <= 100`
     * @operator == - Loose equality (type coercion) - Example: `"5" == 5` is true
     * @operator != - Loose inequality (type coercion) - Example: `"5" != 5` is false
     */
    if (
      src.startsWith('>=', i) ||
      src.startsWith('<=', i) ||
      src.startsWith('==', i) ||
      src.startsWith('!=', i)
    ) {
      out.push({ type: 'op', value: src.slice(i, i + 2), index: i, length: 2 });
      i += 2;
      continue;
    }

    /**
     * @operator > - Greater than - Example: `value > 100`
     * @operator < - Less than - Example: `age < 65`
     * @operator + - Addition (numbers) or concatenation (strings) - Example: `a + b`, uses Decimal.js for precision
     * @operator - - Subtraction or negation - Example: `a - b`, `-value`, uses Decimal.js for precision
     * @operator * - Multiplication - Example: `price * quantity`, uses Decimal.js for precision
     * @operator / - Division - Example: `total / count`, uses Decimal.js for precision
     * @operator % - Modulo (remainder) - Example: `value % 2` (check if even)
     * @operator ! - Logical NOT - Example: `!isActive`, `!(x > 5)`
     * @operator ? - Ternary condition (part 1 of condition ? then : else) OR optional parameter marker - Example: `age >= 18 ? "adult" : "minor"`, `(a, b?) => a + b`
     * @operator : - Ternary separator (part 2 of condition ? then : else)
     */
    if ('><+-*/!?:%'.includes(c)) {
      out.push({ type: 'op', value: c, index: i, length: 1 });
      i++;
      continue;
    }

    /**
     * @lexer punctuation - Structural tokens for syntax
     * @punctuation ; - Statement separator for sequences - Example: `let x = 5; let y = 10; x + y`
     * @punctuation = - Assignment operator - Example: `let total = price * quantity`
     * @punctuation , - Comma separator for function args, arrays, objects - Example: `func(a, b, c)`, `[1, 2, 3]`
     * @punctuation ( ) - Parentheses for grouping and function calls - Example: `(a + b) * c`, `round(value)`
     * @punctuation [ ] - Square brackets for array literals and indexing - Example: `[1, 2, 3]`, `array[0]`
     * @punctuation { } - Curly braces for object literals and match blocks - Example: `{ x: 1, y: 2 }`, `match { ... }`
     * @punctuation . - Dot operator for property access - Example: `user.name`, `root.settings.enabled`
     * @formula @[ ] - Excel-like formula wrapper - Example: `@[sum(prices)]`, `@[age * 2 + 10]`
     */
    if (c === ';') {
      out.push({ type: 'semi', value: ';', index: i, length: 1 });
      i++;
      continue;
    }
    if (c === '=') {
      out.push({ type: 'equals', value: '=', index: i, length: 1 });
      i++;
      continue;
    }
    if (c === ',') {
      out.push({ type: 'comma', value: ',', index: i, length: 1 });
      i++;
      continue;
    }
    if (c === '(' || c === ')') {
      out.push({ type: 'paren', value: c, index: i, length: 1 });
      i++;
      continue;
    }
    if (c === '[' || c === ']') {
      out.push({ type: 'bracket', value: c, index: i, length: 1 });
      i++;
      continue;
    }
    if (c === '{' || c === '}') {
      out.push({ type: 'brace', value: c, index: i, length: 1 });
      i++;
      continue;
    }
    if (c === '.') {
      /** @operator ..= - Inclusive range operator in match patterns - Example: `1..=10 => "low"` */
      if (src.startsWith('..=', i)) {
        out.push({ type: 'op', value: '..=', index: i, length: 3 });
        i += 3;
        continue;
      }
      /** @operator .. - Exclusive range operator in match patterns - Example: `1..10 => "low"` (1 to 9) */
      if (src.startsWith('..', i)) {
        out.push({ type: 'op', value: '..', index: i, length: 2 });
        i += 2;
        continue;
      }
      out.push({ type: 'dot', value: '.', index: i, length: 1 });
      i++;
      continue;
    }

    if (c === '@' && src.charAt(i + 1) === '[') {
      out.push({ type: 'formula', value: '@[', index: i, length: 2 });
      i += 2;
      continue;
    }

    /** @operator in - Membership test - true when the left value is a member of an array or a substring of a string. Supports glob patterns (* = any chars, ? = one char) on either side. Example: `status in ['active', 'pending']`, `code in ['AB*']`, `value in 'hello world'` */
    /** @operator not in - Negated membership - true when the left value is NOT a member of the array or string. Example: `status not in ['deleted', 'archived']`, `code not in ['AB*']` */
    /**
     * @lexer identifier - Variable names, keywords, function names
     * @syntax [A-Za-z_$][A-Za-z0-9_$]*
     * @example value, root, age, userName, sum, $
     * @note Keywords: let (assignment), match (pattern matching), debugger, true, false, null, and, or, not, in
     * @note Special: $ is used as pipeline variable in |> chains
     * @note Special: type operator returns DSL type of value
     */
    if (/[A-Za-z_$]/.test(c)) {
      const start = i;
      let id = c;
      i++;
      while (i < src.length && /[A-Za-z0-9_$]/.test(src.charAt(i))) {
        id += src.charAt(i);
        i++;
      }

      const keywords = [
        'let',
        'match',
        'true',
        'false',
        'null',
        'debugger',
        'and',
        'or',
        'not',
        'in',
        'if',
        'else',
        'then',
        'foreach',
        'when',
        'otherwise',
        'use',
        'return',
      ];
      // Natural-language logical operators are case-insensitive: OR === or, AND === and, NOT === not, IN === in
      const naturalLanguageOps = ['and', 'or', 'not', 'in'];
      const idLower = id.toLowerCase();
      if (naturalLanguageOps.includes(idLower)) {
        out.push({
          type: 'keyword',
          value: idLower,
          index: start,
          length: i - start,
        });
      } else if (keywords.includes(id)) {
        out.push({
          type: 'keyword',
          value: id,
          index: start,
          length: i - start,
        });
      } else if (id === 'type') {
        out.push({ type: 'type', value: id, index: start, length: i - start });
      } else {
        out.push({ type: 'ident', value: id, index: start, length: i - start });
      }
      continue;
    }

    // Template literal: `Hello {name}` - raw content preserved, parser desugars {expr}
    if (c === '`') {
      const start = i;
      i++;
      let raw = '';
      while (i < src.length && src.charAt(i) !== '`') {
        raw += src.charAt(i);
        i++;
      }
      if (src.charAt(i) !== '`')
        throw new ExpressionLexError(
          'Unterminated template literal',
          start,
          src.length - start,
        );
      i++;
      out.push({
        type: 'template',
        value: raw,
        index: start,
        length: i - start,
      });
      continue;
    }

    throw new ExpressionLexError(`Unexpected character '${c}'`, i, 1);
  }

  return out;
}
